import {
  Bar,
  CartesianGrid,
  Legend,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';

import { renderLegend, RootContainer, TitleContainer } from '@components/charts/shared';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { formatNumber } from '@utils/numbers';

const BORDER_RADIUS_PX = 4;
const BORDER_RADIUS_HORIZONTAL: [number, number, number, number] = [
  0,
  BORDER_RADIUS_PX,
  BORDER_RADIUS_PX,
  0,
];
const BORDER_RADIUS_VERTICAL: [number, number, number, number] = [
  BORDER_RADIUS_PX,
  BORDER_RADIUS_PX,
  0,
  0,
];
const BORDER_RADIUS_NONE: [number, number, number, number] = [0, 0, 0, 0];
const BORDER_RADIUS_CSS_HORIZONTAL = `0 ${BORDER_RADIUS_PX}px ${BORDER_RADIUS_PX}px 0`;
const BORDER_RADIUS_CSS_VERTICAL = `${BORDER_RADIUS_PX}px ${BORDER_RADIUS_PX}px 0 0`;
const BORDER_RADIUS_CSS_NONE = '0';

const APPROX_CHAR_WIDTH = 7; // Approximate character width at 0.75rem font size
const LABEL_PADDING = 16; // 8px padding on each side

// Bar label styling constants (semibold12 equivalent for SVG text elements)
const LABEL_FONT_SIZE = '0.75rem'; // 12px
const LABEL_FONT_WEIGHT = 600; // semibold

type DataRecord = Record<string, unknown>;

export type BarDefinition<D extends DataRecord> = {
  dataKey: keyof D;
  color: Color;
  title: string;
};

export type BarChartProps<D extends DataRecord = DataRecord> = {
  data: D[];
  category: keyof D | string;
  categoryLabelMap?: Record<string, string>;
  bars: BarDefinition<D>[];
  isVertical?: boolean;
  height?: number;
  hideLegend?: boolean;
  showTooltip?: boolean;
  title?: string;
  // Minimum pixel gap between X ticks to auto-skip labels
  xMinTickGap?: number;
  // Tuning for bar density/spacing when many categories
  barCategoryGap?: number | string;
  barGap?: number | string;
  // Whether the both axis should show decimals numbers
  allowDecimals?: boolean;
  stacked?: boolean;
  // Show category labels on bars (useful for vertical charts with long labels)
  showBarLabels?: boolean;
  // Optional key for additional label in tooltip (e.g., date, category, ID)
  tooltipLabelKey?: keyof D;
};

type AxisTickProps = {
  x: number;
  y: number;
  payload: {
    value: string | number;
  };
};

const getLabel = (raw: unknown, map?: DataRecord) => map?.[String(raw)] ?? String(raw);

export function BarChart<D extends DataRecord = DataRecord>({
  data,
  category,
  bars,
  isVertical = false,
  categoryLabelMap,
  height = 320,
  hideLegend = false,
  showTooltip = false,
  title,
  xMinTickGap = 12,
  barCategoryGap = '8%',
  barGap = '2%',
  allowDecimals = false,
  stacked = false,
  showBarLabels = false,
  tooltipLabelKey,
}: BarChartProps<D>) {
  const calculateTotal = (payload: D) => {
    if (!stacked) return 0;
    return bars.reduce((sum, bar) => {
      const value = payload[bar.dataKey];
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);
  };

  const margin = isVertical
    ? {
        top: 16,
        right: 24,
        bottom: 16,
        left: showBarLabels ? 40 : 120,
      }
    : {
        top: 24,
        right: 24,
        bottom: 32,
        left: 16,
      };

  const XAxisTick = (props: AxisTickProps) => {
    const { x, y, payload } = props;
    const label = getLabel(payload.value, categoryLabelMap);
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="middle"
          fill={theme.color.textPrimary}
          fontWeight={400}
          fontSize="0.75rem"
          fontFamily={theme.font.family}
        >
          {label}
        </text>
      </g>
    );
  };

  const YAxisTick = (props: AxisTickProps) => {
    const { x, y, payload } = props;
    const label = getLabel(payload.value, categoryLabelMap);
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={-24}
          y={0}
          dy={4}
          textAnchor="start"
          fill={theme.color.textPrimary}
          fontWeight={400}
          fontSize="0.75rem"
          fontFamily={theme.font.family}
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <RootContainer $height={height}>
      {title && (
        <TitleContainer>
          <Text text={title} font="bold14" color="textSecondary" caps />
        </TitleContainer>
      )}
      <ResponsiveContainer>
        <RechartsBarChart
          data={data}
          layout={isVertical ? 'vertical' : 'horizontal'}
          margin={margin}
          barCategoryGap={barCategoryGap}
          {...(!stacked && { barGap })}
        >
          <CartesianGrid
            vertical={isVertical}
            horizontal={!isVertical}
            stroke={theme.color.uiTrans10}
          />

          {isVertical ? (
            <>
              <YAxis
                type="category"
                dataKey={category as string}
                tick={showBarLabels ? false : YAxisTick}
                axisLine={false}
                tickLine={false}
                interval={0}
                allowDecimals={allowDecimals}
              />
              <XAxis
                type="number"
                tick={XAxisTick}
                axisLine={false}
                tickLine={false}
                interval={0}
                allowDecimals={allowDecimals}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={category as string}
                tick={XAxisTick}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={xMinTickGap}
                allowDecimals={allowDecimals}
              />
              <YAxis
                tick={YAxisTick}
                axisLine={false}
                tickLine={false}
                allowDecimals={allowDecimals}
              />
            </>
          )}

          {!hideLegend && (
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={12}
              content={renderLegend}
            />
          )}

          {bars.map((bar, index) => (
            <Bar
              key={bar.dataKey as string}
              dataKey={bar.dataKey as string}
              {...(stacked && { stackId: 'stack' })}
              fill={theme.color[bar.color]}
              color={bar.color}
              name={bar.title}
              radius={
                stacked
                  ? index === bars.length - 1
                    ? isVertical
                      ? BORDER_RADIUS_HORIZONTAL
                      : BORDER_RADIUS_VERTICAL
                    : BORDER_RADIUS_NONE
                  : isVertical
                  ? BORDER_RADIUS_HORIZONTAL
                  : BORDER_RADIUS_VERTICAL
              }
              shape={(props: unknown) => {
                const { x, y, width, height, payload } = props as {
                  x: number;
                  y: number;
                  width: number;
                  height: number;
                  payload: D;
                };
                const value = payload[bar.dataKey];
                const totalValue = stacked ? calculateTotal(payload) : 0;
                const tooltipLabel = tooltipLabelKey
                  ? String(payload?.[tooltipLabelKey] ?? '')
                  : undefined;

                const isTopBar = index === bars.length - 1;
                const isFirstBar = index === 0;
                const borderRadius = stacked
                  ? isTopBar
                    ? isVertical
                      ? BORDER_RADIUS_CSS_HORIZONTAL
                      : BORDER_RADIUS_CSS_VERTICAL
                    : BORDER_RADIUS_CSS_NONE
                  : isVertical
                  ? BORDER_RADIUS_CSS_HORIZONTAL
                  : BORDER_RADIUS_CSS_VERTICAL;

                // Get category label for this bar
                const categoryLabel: string | null =
                  isVertical && showBarLabels && isFirstBar
                    ? String(getLabel(payload[category], categoryLabelMap))
                    : null;

                // Calculate if label fits inside the bar
                const labelWidth = categoryLabel ? categoryLabel.length * APPROX_CHAR_WIDTH : 0;
                const minRequired = labelWidth + LABEL_PADDING;
                const labelFitsInside = width >= minRequired;

                if (!showTooltip) {
                  return (
                    <g>
                      <foreignObject x={x} y={y} width={width} height={height}>
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: theme.color[bar.color],
                            borderRadius,
                            cursor: 'default',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {categoryLabel && labelFitsInside && <BarLabel>{categoryLabel}</BarLabel>}
                        </div>
                      </foreignObject>
                      {categoryLabel && !labelFitsInside && (
                        <text
                          x={x + width + 8}
                          y={y + height / 2}
                          dy={4}
                          fill={theme.color.textPrimary}
                          fontFamily={theme.font.family}
                          fontWeight={LABEL_FONT_WEIGHT}
                          fontSize={LABEL_FONT_SIZE}
                          textAnchor="start"
                        >
                          {categoryLabel}
                        </text>
                      )}
                    </g>
                  );
                }

                return (
                  <g>
                    <foreignObject x={x} y={y} width={width} height={height}>
                      <Tooltip
                        content={
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {stacked ? (
                              <>
                                <span>
                                  {bar.title}: {formatNumber(value as number)}
                                </span>
                                <span style={{ opacity: 0.8 }}>
                                  Total: {formatNumber(totalValue)}
                                </span>
                              </>
                            ) : (
                              <span>{formatNumber(value as number)}</span>
                            )}
                            {tooltipLabel && <span style={{ opacity: 0.8 }}>{tooltipLabel}</span>}
                          </div>
                        }
                        placement="top"
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: theme.color[bar.color],
                            borderRadius,
                            cursor: 'pointer',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {categoryLabel && labelFitsInside && <BarLabel>{categoryLabel}</BarLabel>}
                        </div>
                      </Tooltip>
                    </foreignObject>
                    {categoryLabel && !labelFitsInside && (
                      <text
                        x={x + width + 8}
                        y={y + height / 2}
                        dy={4}
                        fill={theme.color.textPrimary}
                        fontFamily={theme.font.family}
                        fontWeight={LABEL_FONT_WEIGHT}
                        fontSize={LABEL_FONT_SIZE}
                        textAnchor="start"
                      >
                        {categoryLabel}
                      </text>
                    )}
                  </g>
                );
              }}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </RootContainer>
  );
}

const BarLabel = styled.span({
  position: 'absolute',
  left: pxToRem(8),
  font: theme.font.semibold12,
  fontFamily: theme.font.family,
  color: theme.color.white100, // Always white for contrast on colored bars
  whiteSpace: 'nowrap',
});

export default BarChart;
